import { useEffect, useState, useCallback } from "react";
import { X, Share, Plus, CheckCircle2, Download, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { usePWAInstall, isIOSSafari } from "../lib/pwa";

const STORAGE_KEY = "jdsn_pwa_install_dismissed_at";
const DISMISS_DAYS = 14;
const SHOW_AFTER_MS = 1500;

function recentlyDismissed() {
  try {
    const ts = parseInt(localStorage.getItem(STORAGE_KEY) || "0", 10);
    if (!ts) return false;
    return Date.now() - ts < DISMISS_DAYS * 86400 * 1000;
  } catch { return false; }
}

export default function PWAInstallBanner() {
  const { canInstall, isStandalone: standalone, isIOS, promptInstall } = usePWAInstall();
  const [visible, setVisible] = useState(false);
  const [iosOpen, setIosOpen] = useState(false);

  useEffect(() => {
    if (standalone || recentlyDismissed()) return;
    // Android/Chrome: show as soon as we have the deferred prompt
    if (canInstall) {
      const t = setTimeout(() => setVisible(true), SHOW_AFTER_MS);
      return () => clearTimeout(t);
    }
    // iOS Safari: no event, show the explanatory banner after a delay
    if (isIOSSafari()) {
      const t = setTimeout(() => setVisible(true), SHOW_AFTER_MS);
      return () => clearTimeout(t);
    }
  }, [canInstall, standalone]);

  // Listen for successful install → confirmation toast
  useEffect(() => {
    const onInstalled = () => {
      setVisible(false);
      setIosOpen(false);
      toast.success("JDSN Lawn Care App Successfully Installed", {
        description: "Look for the JDSN icon on your Home Screen.",
        duration: 6000,
      });
    };
    window.addEventListener("appinstalled", onInstalled);
    return () => window.removeEventListener("appinstalled", onInstalled);
  }, []);

  const dismiss = useCallback(() => {
    try { localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch { /* noop */ }
    setVisible(false);
  }, []);

  const handleInstall = useCallback(async () => {
    if (canInstall) {
      const res = await promptInstall();
      if (res?.outcome === "accepted") {
        // appinstalled handler will fire the toast
      } else if (res?.outcome === "dismissed") {
        dismiss();
      }
      return;
    }
    if (isIOS) setIosOpen(true);
  }, [canInstall, isIOS, promptInstall, dismiss]);

  if (standalone) return null;

  return (
    <>
      {visible && (
        <div
          className="md:hidden fixed left-3 right-3 z-50 animate-in fade-in slide-in-from-bottom-6"
          style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 72px)" }}
          data-testid="pwa-install-banner"
          role="dialog"
          aria-label="Install JDSN Lawn Care"
        >
          <div className="relative rounded-2xl bg-gradient-to-br from-[#0A3825] via-[#0B4029] to-[#062818] text-white p-4 pr-12 shadow-2xl shadow-emerald-900/40 border border-emerald-500/30 overflow-hidden">
            <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-emerald-400/15 blur-2xl pointer-events-none" />
            <button
              type="button"
              onClick={handleInstall}
              data-testid="pwa-install-cta"
              className="relative w-full flex items-center gap-3 active:scale-[0.98] transition-transform"
            >
              <span className="shrink-0 w-12 h-12 rounded-xl bg-white flex items-center justify-center shadow-md shadow-emerald-600/30 overflow-hidden">
                <img src="/icons/jdsn-logo.png" alt="JDSN" className="w-10 h-10 object-contain" />
              </span>
              <span className="flex-1 min-w-0 text-left">
                <span className="block text-[15px] font-extrabold tracking-tight font-[Outfit] leading-tight">
                  Install JDSN Lawn Care
                </span>
                <span className="block mt-0.5 text-[12px] text-emerald-100/90 leading-snug">
                  {canInstall
                    ? "Tap to install — works like a real app."
                    : isIOS
                      ? "Tap to see how to add to Home Screen."
                      : "Tap to install for one-tap access."}
                </span>
              </span>
              <ChevronRight className="w-5 h-5 text-emerald-100/80 shrink-0" />
            </button>
            <button
              type="button"
              onClick={dismiss}
              data-testid="pwa-install-close"
              aria-label="Dismiss"
              className="absolute top-1/2 -translate-y-1/2 right-2 w-9 h-9 rounded-full hover:bg-white/10 active:bg-white/15 flex items-center justify-center transition-colors"
            >
              <X className="w-5 h-5 text-white/80" />
            </button>
          </div>
        </div>
      )}

      <IOSInstallSheet open={iosOpen} onClose={() => setIosOpen(false)} />
    </>
  );
}

/* ---------------- iOS step-by-step instructions ---------------- */
export function IOSInstallSheet({ open, onClose }) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[60] bg-black/55 flex items-end sm:items-center justify-center p-3"
      onClick={onClose}
      data-testid="pwa-ios-sheet"
    >
      <div
        className="bg-white w-full max-w-md rounded-3xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-gradient-to-br from-[#062818] to-[#0B4029] text-white px-6 py-5 flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center overflow-hidden">
            <img src="/icons/jdsn-logo.png" alt="JDSN" className="w-10 h-10 object-contain" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.2em] font-extrabold text-emerald-200/90">Install on iPhone</div>
            <div className="text-lg font-extrabold tracking-tight font-[Outfit]">Add to Home Screen</div>
          </div>
          <button onClick={onClose} aria-label="Close" className="w-9 h-9 rounded-full hover:bg-white/10 flex items-center justify-center">
            <X className="w-5 h-5" />
          </button>
        </div>
        <ol className="px-5 py-5 space-y-3">
          <Step n={1} icon={Share} title="Tap the Share button">
            <span className="text-gray-600">Look at the bottom of Safari for the </span>
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 font-bold">
              <Share className="w-3.5 h-3.5" /> Share
            </span>
            <span className="text-gray-600"> icon.</span>
          </Step>
          <Step n={2} icon={Plus} title='Tap "Add to Home Screen"'>
            <span className="text-gray-600">Scroll down inside the Share sheet until you see </span>
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-800 font-bold">
              <Plus className="w-3.5 h-3.5" /> Add to Home Screen
            </span>
            .
          </Step>
          <Step n={3} icon={CheckCircle2} title='Tap "Add"'>
            <span className="text-gray-600">The JDSN icon will appear on your home screen — open it like any other app.</span>
          </Step>
        </ol>
        <div className="px-5 pb-5">
          <button
            onClick={onClose}
            data-testid="pwa-ios-sheet-close"
            className="w-full py-3 rounded-2xl bg-[#047857] hover:bg-[#065F46] text-white font-extrabold tracking-tight"
          >
            Got it
          </button>
          <div className="mt-3 text-center text-[11px] text-gray-500">
            iOS Safari only — Chrome/Firefox on iPhone can&apos;t add to Home Screen.
          </div>
        </div>
      </div>
    </div>
  );
}

function Step({ n, icon: Icon, title, children }) {
  return (
    <li className="flex items-start gap-3" data-testid={`pwa-step-${n}`}>
      <div className="shrink-0 w-9 h-9 rounded-full bg-emerald-50 text-[#047857] flex items-center justify-center font-extrabold border border-emerald-100">
        {n}
      </div>
      <div className="flex-1 min-w-0 pt-0.5">
        <div className="text-sm font-extrabold text-gray-900 flex items-center gap-1.5">
          <Icon className="w-4 h-4 text-[#047857]" /> {title}
        </div>
        <div className="mt-1 text-sm leading-snug">{children}</div>
      </div>
    </li>
  );
}

/* ---------------- Reusable "Install App" button ---------------- */
export function InstallAppButton({ className = "", variant = "menu" }) {
  const { canInstall, isStandalone: standalone, isIOS, promptInstall } = usePWAInstall();
  const [iosOpen, setIosOpen] = useState(false);

  if (standalone) return null;
  if (!canInstall && !isIOS) return null;     // no install path on this browser

  const onClick = async () => {
    if (canInstall) {
      const res = await promptInstall();
      if (res?.outcome === "accepted") {
        toast.success("Installing JDSN Lawn Care…");
      }
      return;
    }
    setIosOpen(true);
  };

  if (variant === "row") {
    return (
      <>
        <button
          onClick={onClick}
          data-testid="install-app-row"
          className={`w-full flex items-center gap-3 px-5 py-4 hover:bg-gray-50 text-left ${className}`}
        >
          <span className="w-10 h-10 rounded-full bg-emerald-50 text-[#047857] flex items-center justify-center shrink-0">
            <Download className="w-5 h-5" />
          </span>
          <span className="flex-1 min-w-0">
            <span className="block text-[15px] font-extrabold tracking-tight text-gray-900">Install App</span>
            <span className="block text-[12px] text-gray-500">One-tap home-screen icon, offline support</span>
          </span>
          <ChevronRight className="w-4 h-4 text-gray-400" />
        </button>
        <IOSInstallSheet open={iosOpen} onClose={() => setIosOpen(false)} />
      </>
    );
  }

  return (
    <>
      <button
        onClick={onClick}
        data-testid="install-app-btn"
        className={`inline-flex items-center gap-2 px-3 py-2 rounded-full bg-[#047857] text-white text-sm font-extrabold hover:bg-[#065F46] active:scale-95 transition ${className}`}
      >
        <Download className="w-4 h-4" /> Install App
      </button>
      <IOSInstallSheet open={iosOpen} onClose={() => setIosOpen(false)} />
    </>
  );
}
